// SessionController — the single seam the UI touches. It owns the segmenter
// instance, wires ManagedSession's normalized callbacks into the store, and
// attaches persistence. Created once per session (held in a ref by Session.tsx).

import { createBridgeClient, type BridgeClient } from '@/lib/bridge/bridgeClient'
import { createSpeechController, type SpeechController } from '@/lib/ai/speech'
import type { ProviderId, SpeechmaticsRegion } from '@/lib/keys/types'
import type { SpeakerId } from '@/lib/transcription/types'
import { createIdFactory, maxIdSuffix } from '@/lib/transcription/ids'
import { getProvider } from '@/lib/transcription/providers/registry'
import {
  createManagedSession,
  type ManagedSession
} from '@/lib/transcription/session'
import {
  createSegmenter,
  type SegmentationConfig
} from '@/lib/transcription/segmenter'
import { getSegmentationParams } from '@/lib/debug/debugParams'
import { attachPersistence } from './sessionPersistence'
import { liveSessions } from './liveSessions'
import { setLeaveGuardActive } from './leaveGuard'
import {
  createSessionStore,
  type SessionInit,
  type SessionStore
} from './sessionStore'

export interface SessionController {
  store: SessionStore
  /** The bridge client, or null when no bridge pairing is configured. */
  ai: BridgeClient | null
  /** The resolved gateway origin + token (for the Debug Panel's own debug-stream
   *  / config calls), or null when no bridge pairing is configured. */
  bridge: { base: string; token: string } | null
  /** The AI's spoken-voice controller, or null when no ElevenLabs key is set. */
  speech: SpeechController | null
  start(): Promise<void>
  pause(): Promise<void>
  resume(): Promise<void>
  stop(): Promise<void>
  /** Merge `remove` into `keep` across BOTH the segmenter (future tokens +
   *  internal finals) and the store (visible transcript) so the relabel sticks.
   *  Routing this through the store alone is reverted on the next final batch. */
  mergeSpeakers(keep: SpeakerId, remove: SpeakerId): void
  /** Live-tune the segmenter's F10 thresholds (the Debug Panel). */
  setSegmentation(partial: Partial<SegmentationConfig>): void
  /** Detach persistence; call when the session view unmounts. */
  dispose(): void
}

export interface SessionControllerOptions {
  init: SessionInit
  providerId: ProviderId
  apiKey: string
  region?: SpeechmaticsRegion
  deviceId?: string
  /** Pre-acquired tab/system audio (getDisplayMedia) to mix into capture, so a
   *  Google Meet / Zoom tab's remote participants land in the transcript. */
  tabStream?: MediaStream | null
  sonioxDirectKey?: boolean
  maxSpeakers?: number
  /** Bridge pairing; when present, the Claude Code participant runs (PRD 007). */
  bridge?: {
    /** Gateway origin; empty = same origin (gateway-served SPA). */
    endpoint?: string
    token: string
    /** Direct-address config (PRD 004): the room's name for the AI. */
    triggerName?: string
  }
  /** ElevenLabs voice config; when present, the AI can speak its cards aloud. */
  tts?: {
    apiKey: string
    voiceId: string
    /** Output device for the voice (setSinkId); undefined = system default. */
    outputDeviceId?: string
  }
}

export function createSessionController(
  opts: SessionControllerOptions
): SessionController {
  const store = createSessionStore(opts.init)
  const detachPersistence = attachPersistence(store)
  liveSessions.register(store)

  // Resume a restored session past its existing ids so the fresh segmenter never
  // re-mints an id that's already in the transcript, then seed it with the
  // restored finals so the first post-resume final batch APPENDS rather than
  // wiping the history (the store re-slices the segmenter's finals on every
  // batch). A brand-new session has no finals/speakers, so both start at 0.
  const restoredFinals = opts.init.finals ?? []
  const mintSegmentId = createIdFactory(
    'seg_',
    maxIdSuffix(restoredFinals.map((s) => s.id), 'seg_')
  )
  const mintSpeakerId = createIdFactory(
    'spk_',
    maxIdSuffix(Object.keys(opts.init.speakers ?? {}), 'spk_')
  )
  const segmenter = createSegmenter({
    mintSegmentId,
    mintSpeakerId,
    // Seed the F10 thresholds from the persisted Debug-Panel params (defaults =
    // today's behavior when unset); live-tunable via setSegmentation.
    segmentation: getSegmentationParams()
  })
  if (restoredFinals.length) segmenter.seed(restoredFinals)

  const ai = opts.bridge
    ? createBridgeClient({
      store,
      endpoint: opts.bridge.endpoint,
      token: opts.bridge.token,
      triggerName: opts.bridge.triggerName
    })
    : null

  const speech = opts.tts?.apiKey
    ? createSpeechController({
      store,
      config: {
        apiKey: opts.tts.apiKey,
        voiceId: opts.tts.voiceId,
        outputDeviceId: opts.tts.outputDeviceId
      }
    })
    : null

  const provider = getProvider(opts.providerId, {
    sonioxDirectKey: opts.sonioxDirectKey
  })

  const managed: ManagedSession = createManagedSession({
    provider,
    apiKey: opts.apiKey,
    deviceId: opts.deviceId,
    tabStream: opts.tabStream,
    region: opts.region,
    maxSpeakers: opts.maxSpeakers,
    callbacks: {
      onResult(result) {
        const out = segmenter.ingest(result)
        for (const sp of out.newSpeakers) {
          store.ensureSpeaker(sp.speakerId, sp.providerLabel)
        }
        store.applyResult(out.finals, out.interim, out.finalsChanged)
      },
      onState(state, detail) {
        store.setConnection(state, detail)
        if (state === 'live') store.setError(null)
        // Drive the bridge round off capture state so a restored/idle session
        // never floods the gateway, and leaving makes a clean cut (PRD 007):
        //   live   → activate (or resume forwarding after a pause)
        //   paused → soft-stop forwarding; the round stays open, the agent parks
        //            idle, and a resume picks straight back up
        //   closed/error → end the round; the agent stops listening
        // While a round is live the leave guard warns before navigating away.
        if (ai) {
          if (state === 'live') {
            ai.activate()
            setLeaveGuardActive(true)
          } else if (state === 'paused') {
            ai.setForwarding(false)
          } else if (state === 'closed' || state === 'error') {
            ai.dispose()
            setLeaveGuardActive(false)
          }
        }
      },
      onError(err) {
        store.setError(err)
      },
      onLevel(level) {
        store.setLevel(level)
      }
    }
  })

  // Same default-to-same-origin resolution as bridgeClient, surfaced so the
  // Debug Panel can reach /bridge/debug + /bridge/config without re-deriving it.
  const bridge = opts.bridge
    ? {
      base: (opts.bridge.endpoint || window.location.origin).replace(/\/+$/, ''),
      token: opts.bridge.token
    }
    : null

  return {
    store,
    ai,
    bridge,
    speech,
    start: () => managed.start(),
    pause: () => managed.pause(),
    resume: () => managed.resume(),
    mergeSpeakers(keep, remove) {
      // Order matters only for clarity; both touch independent state. Segmenter
      // first re-routes future tokens + its internal finals, then the store
      // rewrites the currently-rendered transcript for an immediate update.
      segmenter.mergeSpeaker(remove, keep)
      store.mergeSpeakers(keep, remove)
    },
    setSegmentation(partial) {
      segmenter.setConfig(partial)
    },
    async stop() {
      await managed.stop()
    },
    dispose() {
      // Release the mic/socket if still running, then stop mirroring to IDB.
      void managed.stop()
      ai?.dispose()
      speech?.dispose()
      setLeaveGuardActive(false)
      detachPersistence()
      liveSessions.unregister(store.getSnapshot().id)
    }
  }
}
